'use client';

import type { IUser } from '@repo/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { searchUsersAction } from './search-users-action';

interface ICommandPaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  results: IUser[];
  isSearching: boolean;
}

export function useCommandPalette(enabled: boolean): ICommandPaletteState {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<IUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement).tagName;
      const isEditable =
        tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable;
      if (e.key === '/' && !isEditable) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 3) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      const users = await searchUsersAction(value);
      setResults(users);
      setIsSearching(false);
    }, 300);
  }, []);

  const handleSetOpen = useCallback((value: boolean) => {
    setOpen(value);
    if (!value) {
      setSearch('');
      setResults([]);
      setIsSearching(false);
    }
  }, []);

  return {
    open,
    setOpen: handleSetOpen,
    search,
    setSearch: handleSearchChange,
    results,
    isSearching,
  };
}
